'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { popModal } from '@/components/modals'
import { DISCORD_URL } from '@/lib/constant'
import { useTranslations } from 'next-intl'
import sendToBothAnalytics from '@/lib/mixpanel/sendToBothAnalytics'
import { MIXPANEL_CLIENT_EVENT_NAMES } from '@/lib/mixpanel/constant'
import { useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { useCurrentUser } from '@/hooks/use-current-user'
import { useProjectStore } from '@/hooks/use-project-store'
import { useSearchParams } from 'next/navigation'

export default function DiscordModal() {
  const t = useTranslations('modals.discord')
  const dismissedRef = useRef(false)
  const causeRef = useRef<null | 'join' | 'close_x' | 'overlay' | 'other'>(null)
  const { data: user } = useCurrentUser()
  const userId = user?._id ? String(user._id) : undefined
  const { getProjectId } = useProjectStore()
  const searchParams = useSearchParams()
  const projectId = getProjectId?.() || searchParams.get('id') || undefined

  const track = (action: string, extra?: Record<string, any>) => {
    try {
      sendToBothAnalytics(
        MIXPANEL_CLIENT_EVENT_NAMES.USER_DISCORD_POPUP,
        {
          action,
          user_id: userId,
          project_id: projectId,
          ...extra,
        },
        userId,
      )
    } catch {}
  }
  const persistDismiss = async () => {
    try {
      await fetch('/api/onboarding/dismiss', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ survey: 'discord_invite' }),
      })
    } catch {}
  }

  useEffect(() => {
    // View
    track('view')

    // Track overlay click specifically
    const overlay = document.querySelector(
      '.discord-overlay',
    ) as HTMLElement | null
    const handleOverlayClick = () => {
      causeRef.current = 'overlay'
      track('dismiss_overlay')
    }
    overlay?.addEventListener('click', handleOverlayClick)
    return () => overlay?.removeEventListener('click', handleOverlayClick)
  }, [])

  const handleOpenChange = async (open: boolean) => {
    if (!open) {
      if (!dismissedRef.current) {
        dismissedRef.current = true
        await persistDismiss()
      }
      // Fallback for ESC/programmatic dismiss
      if (!causeRef.current) {
        causeRef.current = 'other'
        track('dismiss_other')
      }
      popModal()
    }
  }

  return (
    <Dialog open={true} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[480px] max-w-[calc(100vw-10px)] w-[calc(100vw-10px)] sm:w-auto bg-[#222] text-white"
        noCloseButton
        overlayClassName="discord-overlay"
      >
        <button
          type="button"
          className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none"
          aria-label="Close"
          onClick={async () => {
            causeRef.current = 'close_x'
            track('close_button')
            if (!dismissedRef.current) {
              dismissedRef.current = true
              await persistDismiss()
            }
            popModal()
          }}
        >
          <X className="h-6 w-6" />
        </button>
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('title')}</DialogTitle>
          <DialogDescription className="text-[#d9d9d9]">
            {t('description')}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex justify-center">
          <Button
            className="bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-3"
            onClick={async () => {
              causeRef.current = 'join'
              track('click_join')
              window.open(DISCORD_URL, '_blank')
              if (!dismissedRef.current) {
                dismissedRef.current = true
                await persistDismiss()
              }
              popModal()
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-label="Discord"
              role="img"
            >
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
            {t('join_button')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
